import React from 'react'

import { ModalInjectedProps, ConfirmationDialog } from '@sketch/components'
import {
  useUnpublishShareMutation,
  GetProfilePublicationsDocument,
  GetProfilePublicationsQueryVariables,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'

interface UnpublishPublicationModalProps extends ModalInjectedProps {
  shareName: string
  shareIdentifier: string
  workspaceIdentifier?: string
  publicCommentsEnabled?: boolean
  publicInspectEnabled?: boolean
  onUnpublish?: () => void
}

export const UnpublishPublicationModal = ({
  hideModal,
  shareName,
  shareIdentifier,
  workspaceIdentifier,
  publicCommentsEnabled = false,
  publicInspectEnabled = false,
  onUnpublish,
}: UnpublishPublicationModalProps) => {
  const title = 'Unpublish Document from Profile?'
  const { showToast } = useToast()

  const [unpublishShare, { loading: isUnpublishing }] =
    useUnpublishShareMutation({
      onError: 'show-toast',
      onCompleted: () => {
        showToast(`"${shareName}" successfully unpublished from profile`)
        hideModal()
        onUnpublish?.()
      },
      refetchQueries: [
        {
          query: GetProfilePublicationsDocument,
          variables: {
            workspaceIdentifier,
          } as GetProfilePublicationsQueryVariables,
        },
      ],
      awaitRefetchQueries: true,
    })

  const handleConfirm = async () => {
    try {
      await unpublishShare({
        variables: {
          shareIdentifier,
          publicAccessLevel: 'NONE',
          publicCommentsEnabled,
          publicInspectEnabled,
        },
      })
    } catch (error) {
      // Error is already handled by onError: 'show-toast'
      // Modal will remain open for user to retry
    }
  }

  return (
    <ConfirmationDialog
      hideModal={hideModal}
      title={title}
      onConfirm={handleConfirm}
      confirmButton={{
        text: 'Unpublish',
        variant: 'primary',
        loading: isUnpublishing,
      }}
      cancelButton={{
        text: 'Cancel',
        disabled: isUnpublishing,
      }}
    >
      <p>
        &ldquo;<strong>{shareName}</strong>&rdquo; will be unpublished from the
        Workspace profile.
      </p>
    </ConfirmationDialog>
  )
}
